export interface DonutSlice {
  label: string;
  value: number;
  color: string;
}

/** Inline-SVG donut with a centred headline (e.g. pass rate). */
export function Donut({
  slices,
  size = 148,
  thickness = 16,
  center,
  caption,
}: {
  slices: DonutSlice[];
  size?: number;
  thickness?: number;
  center: string;
  caption?: string;
}) {
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const total = slices.reduce((s, x) => s + x.value, 0);
  let offset = 0;
  const description = slices.map((s) => `${s.label}: ${s.value}`).join(', ');

  return (
    <div className="relative inline-grid place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${center} ${caption ?? ''}. ${description}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={thickness} className="stroke-slate-100 dark:stroke-slate-800" />
        {total > 0 &&
          slices
            .filter((s) => s.value > 0)
            .map((s) => {
              const len = (s.value / total) * c;
              const el = (
                <circle
                  key={s.label}
                  cx={size / 2}
                  cy={size / 2}
                  r={r}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={thickness}
                  strokeDasharray={`${len} ${c - len}`}
                  strokeDashoffset={-offset}
                  transform={`rotate(-90 ${size / 2} ${size / 2})`}
                  style={{ transition: 'stroke-dasharray 400ms ease' }}
                />
              );
              offset += len;
              return el;
            })}
      </svg>
      <div className="absolute inset-0 grid place-content-center text-center">
        <div className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{center}</div>
        {caption && <div className="text-xs text-slate-500 dark:text-slate-400">{caption}</div>}
      </div>
    </div>
  );
}

export const STATUS_COLORS = {
  passed: '#10b981',
  failed: '#f43f5e',
  errored: '#f59e0b',
  skipped: '#94a3b8',
};
